import { ChevronDown } from "@/components/bnp-icons";
import { useEffect, useRef, useState } from "react";

interface DropdownProps {
  options: any[];
  onChange: (event: { target: { name: string; value: string } }) => void;
  error?: string;
  value?: any;
  name: string;
  label?: string;
  hero?: boolean;
  disabled?: boolean;
  searchable?: boolean;
  placeholder?: string;
}

const DropdownMenu = ({
  options,
  onChange,
  error,
  value,
  name,
  hero,
  label,
  disabled,
  searchable = true,
  placeholder = "Search...",
}: DropdownProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const selectRef = useRef<HTMLDivElement>(null);

  const handleToggle = () => setIsOpen(!isOpen);

  const handleSelect = (option: any) => {
    setIsOpen(false);
    onChange({ target: { name, value: option } });
    setSearchTerm(""); // clear search after selection
  };

  const filteredOptions = options.filter((option) =>
    option?.toString().toLowerCase().includes(searchTerm.toLowerCase()),
  );

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        selectRef.current &&
        !selectRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div className="relative w-full" ref={selectRef}>
      {label && (
        <label
          htmlFor={name}
          className={`block mb-1 text-sm font-medium ${
            hero ? "text-white" : "text-gray-700"
          }`}
        >
          {label}
        </label>
      )}

      {/* Trigger button */}
      <div className="relative z-10">
        <button
          type="button"
          onClick={handleToggle}
          disabled={disabled}
          className={`flex justify-between items-center w-full h-12 px-4 py-2 text-left border rounded-lg shadow-sm ${
            isOpen
              ? "border-bnp-cyan"
              : error
                ? "border-red-500"
                : hero
                  ? "border-white bg-transparent"
                  : "border-soft-200 bg-white"
          } transition-colors disabled:opacity-50`}
        >
          <div className="flex flex-col items-start">
            <span
              className={`mt-1 max-w-[150px] truncate text-sm ${
                hero && !value
                  ? "text-white"
                  : hero && value
                    ? "text-white"
                    : !value && !hero
                      ? "text-grey-4"
                      : "text-black"
              }`}
            >
              {hero ? value || "Any" : value || "Select option"}
            </span>
          </div>
          <ChevronDown
            className={`w-4 h-4 text-[#868C98] transition-transform ${
              isOpen ? "rotate-180" : ""
            }`}
          />
        </button>
      </div>

      {/* Dropdown */}
      {isOpen && !disabled && (
        <div className="absolute w-full z-50 mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-hidden">
          {/* Search Input */}
          {searchable && options.length > 4 && (
            <div className="p-2 border-b border-gray-200">
              <input
                type="text"
                placeholder={placeholder}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-bnp-cyan"
              />
            </div>
          )}

          {/* Options List */}
          <ul className="max-h-48 overflow-auto">
            {filteredOptions.length ? (
              filteredOptions.map((option, index) => (
                <li
                  key={index}
                  onClick={() => handleSelect(option)}
                  className={`text-sm px-4 py-2 cursor-pointer ${
                    option === value
                      ? "bg-bnp-teal text-white font-semibold"
                      : "text-grey-7 hover:bg-gray-100"
                  }`}
                >
                  {option}
                </li>
              ))
            ) : (
              <li className="px-4 py-2 text-gray-500 text-sm">
                No matching options
              </li>
            )}
          </ul>
        </div>
      )}

      {/* Error message */}
      {error && <p className="mt-1 text-sm text-red-500">{error}</p>}
    </div>
  );
};

export default DropdownMenu;
